// What a timeline draws (10-timeline.md, "What is in the list"; 13-messages.md, "Event lines",
// "Grouping"): message rows, one-line events in words (17-people.md owns the membership words), and
// the call lines built from the call-membership state events.
import { EventType, MsgType, RelationType, type MatrixClient, type MatrixEvent, type Room } from 'matrix-js-sdk';
import { setting } from '../core/settings';
import { t, type Key } from '../i18n';
import { personName, shownName } from '../people/names';

export const CALL_MEMBER = 'org.matrix.msc3401.call.member';
const MESSAGES = new Set<string>([EventType.RoomMessage, EventType.RoomMessageEncrypted, EventType.Sticker]);

/** A message row, a one-line event, or nothing. */
export type Kind = 'message' | 'line' | undefined;

export function kindOf(client: MatrixClient, ev: MatrixEvent): Kind {
  if (ev.isRedacted() || ev.isRelation(RelationType.Replace) || client.isUserIgnored(ev.getSender() ?? '')) return undefined;
  const type = ev.getType();
  if (MESSAGES.has(type)) return ev.isThreadRoot || !ev.threadRootId ? 'message' : undefined;
  if (type === EventType.RoomMember) {
    const change = memberChange(ev);
    if (!change) return undefined;
    return (change.profile ? setting('hide-profile-changes') : setting('hide-membership')) ? undefined : 'line';
  }
  if (type === EventType.RoomName || type === EventType.RoomTopic || type === EventType.RoomAvatar) return 'line';
  return undefined;
}

/** A counting event (12, rule 4): someone else's message, not deleted, not an edit. */
export const counts = (ev: MatrixEvent, me: string) =>
  (MESSAGES.has(ev.getType()) || ev.getType() === EventType.RoomCreate) && ev.getSender() !== me && !ev.isRedacted() && !ev.isRelation(RelationType.Replace);

/** Drawn under the row before without picture and name (13, "Grouping"). */
export function continues(before: MatrixEvent | undefined, ev: MatrixEvent): boolean {
  if (!before) return false;
  return before.getSender() === ev.getSender() && before.getType() === ev.getType() && Math.abs(ev.getTs() - before.getTs()) < 90_000;
}

/** Same calendar day. */
export const sameDay = (a: MatrixEvent, b: MatrixEvent) => new Date(a.getTs()).toDateString() === new Date(b.getTs()).toDateString();

interface Change {
  key: Key;
  /** A name or picture change only (hidden by default). */
  profile?: boolean;
  /** The names in bold: {a} the sender, {b} the member, {old}/{new} names. */
  names: Record<string, string>;
  icon: 'join' | 'leave' | 'invite' | 'person' | 'ban';
}

/** A membership event in words (17, "Membership changes in words"); knocking lines aren't drawn. */
export function memberChange(ev: MatrixEvent, room?: Room): Change | undefined {
  const now = ev.getContent();
  const before = ev.getPrevContent();
  const a = senderName(ev, room);
  const b = shownName(now.displayname) ?? shownName(ev.target?.rawDisplayName) ?? shownName(before.displayname) ?? personName(room?.client, ev.getStateKey() ?? '', room);
  const self = ev.getSender() === ev.getStateKey();
  const was = before.membership;
  const names = { a, b };
  switch (now.membership) {
    case 'invite':
      return was === 'knock' ? undefined : { key: 'chat.invited', names, icon: 'invite' };
    case 'knock':
      return undefined;
    case 'join':
      if (was !== 'join') return { key: 'chat.joined', names, icon: 'join' };
      if (now.displayname !== before.displayname)
        return now.displayname
          ? { key: 'chat.renamed', names: { old: before.displayname ?? b, new: now.displayname }, profile: true, icon: 'person' }
          : { key: 'chat.nameRemoved', names: { b: before.displayname ?? b }, profile: true, icon: 'person' };
      if (now.avatar_url !== before.avatar_url) return { key: now.avatar_url ? 'chat.pictureChanged' : 'chat.pictureRemoved', names, profile: true, icon: 'person' };
      return { key: 'chat.membershipUpdated', names, icon: 'person' };
    case 'leave':
      if (was === 'invite') return self ? { key: 'chat.declined', names, icon: 'leave' } : { key: 'chat.withdrew', names, icon: 'leave' };
      if (was === 'knock') return undefined;
      if (was === 'ban') return { key: 'chat.unbanned', names, icon: 'person' };
      return self ? { key: 'chat.left', names, icon: 'leave' } : { key: 'chat.removed', names, icon: 'leave' };
    case 'ban':
      return { key: 'chat.banned', names, icon: 'ban' };
    default:
      return { key: 'chat.membershipUpdated', names, icon: 'person' };
  }
}

/** One-line events other than membership: the words and the names in bold. */
export function lineOf(ev: MatrixEvent, room?: Room): Change {
  const a = senderName(ev, room);
  switch (ev.getType()) {
    case EventType.RoomName:
      return { key: 'chat.changedName', names: { a }, icon: 'person' };
    case EventType.RoomTopic:
      return { key: 'chat.changedTopic', names: { a }, icon: 'person' };
    case EventType.RoomAvatar:
      return { key: 'chat.changedPicture', names: { a }, icon: 'person' };
    default:
      return memberChange(ev, room)!;
  }
}

/** Splits a sentence with {names} into text and bold parts. */
export function boldParts(key: Key, names: Record<string, string>, extra: Record<string, string | number> = {}): { text: string; bold: boolean }[] {
  const marked = t(key, { ...extra, ...Object.fromEntries(Object.entries(names).map(([k, v]) => [k, `\u0001${v}\u0001`])) });
  return marked.split('\u0001').map((text, i) => ({ text, bold: i % 2 === 1 }));
}

export interface Call {
  starter: string;
  people: string[];
  start: number;
  end?: number;
}

/** The calls of the loaded events, oldest first (13, "Call lines", rules 1–3). */
export function callsOf(events: MatrixEvent[]): Map<string, Call> {
  const calls = new Map<string, Call>();
  const joined = new Set<string>();
  let call: Call | undefined;
  for (const ev of events) {
    if (ev.getType() !== CALL_MEMBER) continue;
    const key = ev.getStateKey() ?? '';
    const sender = ev.getSender() ?? '';
    if (Object.keys(ev.getContent()).length) {
      if (!call) calls.set(ev.getId()!, (call = { starter: sender, people: [], start: ev.getTs() }));
      else if (sender !== call.starter && !call.people.includes(sender)) call.people.push(sender);
      joined.add(key);
    } else if (joined.delete(key) && call && joined.size === 0) {
      call.end = ev.getTs();
      call = undefined;
    }
  }
  return calls;
}

/** "< 1 min", "12 min", "1 h 5 min". */
export function callLength(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  if (minutes < 1) return t('chat.underMinute');
  if (minutes < 60) return t('chat.minutes', { n: minutes });
  return t('chat.hoursMinutes', { h: Math.floor(minutes / 60), m: minutes % 60 });
}

/** The person's own messages they may edit (13, "Edits"): text, emote or notice, not an edit. */
export function mayEdit(ev: MatrixEvent, me: string): boolean {
  const content = ev.getOriginalContent();
  return (
    ev.getSender() === me &&
    ev.getType() === EventType.RoomMessage &&
    [MsgType.Text, MsgType.Emote, MsgType.Notice].includes(content.msgtype as MsgType) &&
    !ev.isRedacted() &&
    (!content['m.relates_to'] || !!content['m.relates_to']['m.in_reply_to'] || content['m.relates_to'].rel_type === RelationType.Thread)
  );
}

/** A person's name in a room (02, "A person's name"; never their chat ID). */
export const nameIn = (room: Room, userId: string) => personName(room.client, userId, room);

/** The sender's name when the event was sent, else their name now. */
const senderName = (ev: MatrixEvent, room?: Room) => shownName(ev.sender?.rawDisplayName) ?? personName(room?.client, ev.getSender() ?? '', room);
